import React from 'react';
import { useCan } from '../../hooks/useCan';

/**
 * <Can>
 * Declarative permission gating.
 *
 * Props:
 *  - section: string (optional if using requirements)
 *  - level: 'read' | 'write'
 *  - action: string (specific action capability)
 *  - any: array of requirement objects [{ section, level } | { section, action }]
 *  - all: array of requirement objects
 *  - not: requirement object (negates if present and satisfied)
 *  - fallback: ReactNode (rendered when not allowed)
 *  - children: ReactNode or function({ allowed, explain })
 *
 * Priority resolution:
 *  1. any (if provided)
 *  2. all (if provided)
 *  3. (section+level) or (section+action)
 *
 * If children is a function it receives ({ allowed, explain: ()=>{ allowed, reason } }).
 */
export const Can = ({ section, level, action, any, all, not, fallback = null, children }) => {
  const can = useCan();

  let allowed;
  if (any && any.length) {
    allowed = can.any(any);
  } else if (all && all.length) {
    allowed = can.all(all);
  } else if (section && level) {
    allowed = can.section(section, { level });
  } else if (section && action) {
    allowed = can.action(section, action);
  } else {
    allowed = false;
  }

  if (not) {
    const neg = can.section(not.section, not.level ? { level: not.level } : (not.action ? { action: not.action } : undefined));
    if (neg) allowed = false;
  }

  const explainObj = () => {
    if (any && any.length) return { allowed, reason: allowed ? undefined : 'None of the ANY requirements passed' };
    if (all && all.length) return { allowed, reason: allowed ? undefined : 'Not all ALL requirements passed' };
    if (section && level) return can.explain({ section, level });
    if (section && action) return can.explain({ section, action });
    return { allowed: false, reason: 'No valid permission criteria specified' };
  };

  if (typeof children === 'function') {
    return children({ allowed, explain: explainObj });
  }
  return allowed ? <>{children}</> : <>{fallback}</>;
};

/**
 * <HideIf>
 * Inverse of <Can>. Renders children only when the provided requirement(s) are NOT satisfied.
 * Props mirror a subset of <Can>: section, level, action, any, all.
 * Optional fallbackWhenVisible: node to render instead when the permission WOULD have allowed it.
 */
export const HideIf = ({ section, level, action, any, all, children, fallbackWhenVisible = null }) => {
  const can = useCan();
  let allowed;
  if (any && any.length) {
    allowed = can.any(any);
  } else if (all && all.length) {
    allowed = can.all(all);
  } else if (section && level) {
    allowed = can.section(section, { level });
  } else if (section && action) {
    allowed = can.action(section, action);
  } else {
    allowed = false; // nothing specified -> treat as not allowed so we show children (acts as informational hide wrapper)
  }
  return allowed ? <>{fallbackWhenVisible}</> : <>{children}</>;
};

/**
 * withPermission HOC
 * Usage: export default withPermission({ section:'invoice', level:'write' })(Component)
 */
export function withPermission(requirement, options = {}) {
  const { fallback = null } = options;
  return function Wrap(Wrapped) {
    return function PermissionWrapped(props) {
      return (
        <Can {...normalizeRequirement(requirement)} fallback={fallback}>
          <Wrapped {...props} />
        </Can>
      );
    };
  };
}

function normalizeRequirement(req) {
  if (!req) return {};
  if (req.section && (req.level || req.action)) return req;
  if (req.any) return { any: req.any };
  if (req.all) return { all: req.all };
  return {};
}

export default Can;
